/**
 * PageHeader — the single, opinionated page-header pattern used across
 * every admin/podiatrist/provider/agent page.
 *
 * Why a shared component:
 *   Pre-R22, each page hand-rolled its own header. Density tweaks had to
 *   be applied in 18 files and inevitably drifted. This component locks
 *   the layout once: eyebrow + title (h3) + optional inline meta on the
 *   left, optional action button on the right, all in one wrapping row
 *   with mb-3.
 *
 * Layout rules (per Rafael's R21 review item 2):
 *   - Eyebrow renders above the title row.
 *   - Title and `meta` (e.g. "10 matching") sit on the SAME baseline so
 *     the "subtitle" doesn't add a third line.
 *   - Optional `description` (a longer paragraph) goes underneath the
 *     title row, but the parent rarely needs it — most pages just want
 *     the inline meta.
 *   - Right-side `action` slot for the primary CTA.
 *
 * The pattern matches the orders-page header style Rafael called out as
 * the right density for the platform.
 */

import * as React from 'react';
import { Eyebrow, Heading } from '@insolech/ui';

interface Props {
  eyebrow: React.ReactNode;
  title: React.ReactNode;
  /** Inline secondary text rendered next to the title, baseline-aligned. */
  meta?: React.ReactNode;
  /** Longer paragraph rendered beneath the title row. Use sparingly. */
  description?: React.ReactNode;
  /** Right-side primary action (typically a Button). */
  action?: React.ReactNode;
}

export function PageHeader({ eyebrow, title, meta, description, action }: Props) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
      <div className="min-w-0">
        <Eyebrow>{eyebrow}</Eyebrow>
        <div className="flex items-baseline gap-3 flex-wrap mt-1">
          <Heading as="h1" size="h3">
            {title}
          </Heading>
          {meta != null ? (
            <span className="text-sm text-text-secondary">{meta}</span>
          ) : null}
        </div>
        {description != null ? (
          <p className="text-text-secondary mt-1 text-sm max-w-2xl">{description}</p>
        ) : null}
      </div>
      {action != null ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
